import React, { useMemo, useState, useRef, useEffect } from "react";
import { Box, Button, Typography, Tooltip, IconButton } from "@mui/material";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import {
  DescriptionOutlined,
  Close,
  FileDownloadOutlined,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  addLicenseDetails,
  uploadProviderDocument,
  deleteLicenseDetails,
  deleteProviderUploadedDocument,
} from "../../../../api/provider-section/provider-service-management";
import customAxios from "../../../../api";
import InfoPopup from "../../../../common/components/info-popup/info-popup";
import ActionConfirmationPopup from "../../../../common/components/action-confirmation-popup/action-confirmation-popup";
import "./documentation.scss";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ActionsMenu from "../../../../common/components/listing-table/actions-menu";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { AlertComponent } from "../../../../common/components";

interface DocumentationProps {
  data: any;
  refresh: () => void;
}

const TruncatedTooltip: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      setIsOverflowed(element.scrollWidth > element.offsetWidth);
    }
  }, [children]);

  return (
    <Tooltip title={title} disableHoverListener={!isOverflowed} arrow placement="top-start">
      <Typography ref={textRef} className={className}>
        {children}
      </Typography>
    </Tooltip>
  );
};

const Documentation: React.FC<DocumentationProps> = ({ data, refresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [newLicense, setNewLicense] = useState({
    licenceNumber: "",
    expirationDate: null as Dayjs | null,
    file: null as File | null,
  });
  const [dateError, setDateError] = useState<string | null>(null);

  const [localLicenses, setLocalLicenses] = useState<any[]>([]);
  const [infoPopup, setInfoPopup] = useState({
    open: false,
    title: "",
    note: "",
  });
  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    title: "",
    note: "",
    onOkay: () => { },
  });
  // For ActionsMenu on uploaded documents
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  // ActionsMenu options for uploaded documents
  const getDocMenuOptions = [
    { label: "View", value: "view", id: 1, disabled: false },
    { label: "Download", value: "download", id: 2, disabled: false },
    {
      label: "Delete",
      value: "delete",
      id: 3,
      disabled: false,
      tooltipText: "Delete this document",
    },
  ];
  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const handleDocMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    doc: any,
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedDoc(doc);
  };

  const handleDocMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedDoc(null);
  };
  const handleDeleteDocument = async (documentId: number | string) => {
    setIsLoading(true);
    try {
      await deleteProviderUploadedDocument(documentId);
      if (refresh) refresh();
      setAlertMessage("Document deleted successfully.");
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Error deleting document.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDocMenuOptionClick = (option: any) => {
    if (!selectedDoc) return;
    if (option.value === "view") {
      window.open(selectedDoc.url, "_blank");
    } else if (option.value === "download") {
      handleDownload(
        selectedDoc.url,
        selectedDoc.fileName || selectedDoc.name || "document",
      );
    } else if (option.value === "delete") {
      setConfirmPopup({
        open: true,
        title: "Delete Document",
        note: `Are you sure you want to delete ${selectedDoc.fileName || selectedDoc.name || "this"} document?`,
        onOkay: async () => {
          handleDeleteDocument(selectedDoc.fileId || selectedDoc.id);
          setConfirmPopup((prev) => ({ ...prev, open: false }));
        },
      });
    }
    handleDocMenuClose();
  };

  React.useEffect(() => {
    const licenses =
      data?.providerLicenceDocuments || data?.partnerLicenceDocuments || [];
    setLocalLicenses(licenses);
    if (licenses.length === 0) {
      setShowForm(true);
    } else {
      setShowForm(false);
    }
  }, [data]);

  const handleLicenseChange = (field: string, value: any) => {
    setNewLicense((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddLicense = async () => {
    if (
      !newLicense.licenceNumber ||
      !newLicense.expirationDate ||
      !newLicense.expirationDate.isValid() ||
      newLicense.expirationDate.isBefore(dayjs(), "day") ||
      !newLicense.file
    ) {
      setInfoPopup({
        open: true,
        title: "Invalid Information",
        note: "Please provide a valid license number, a future expiration date, and upload a file.",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = new FormData();
      payload.append("licence_document", newLicense.file);
      payload.append("licenceNumber", newLicense.licenceNumber);
      payload.append("expiryDate", newLicense.expirationDate.toISOString());

      const response = await addLicenseDetails(payload);

      if (response.data.success) {
        setNewLicense({ licenceNumber: "", expirationDate: null, file: null });
        setShowForm(false);
        refresh();
      } else {
        setInfoPopup({
          open: true,
          title: "Error",
          note: "Failed to add license. Please try again.",
        });
      }
    } catch (error: any) {
      console.error("ERROR IN ADDING LICENSE DETAILS::", error);
      const errorData = error?.response?.data;
      if (errorData?.error === "FILE_TOO_LARGE") {
        setAlertMessage(`${errorData.message}. ${errorData.details}`);
        setAlertType("error");
        setAlertOpen(true);
      } else {
        setInfoPopup({
          open: true,
          title: "Error",
          note: errorData?.message || "An error occurred while adding the license.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLicense = async (id: number) => {
    setConfirmPopup({
      open: true,
      title: "Delete License",
      note: "Are you sure you want to delete this license?",
      onOkay: async () => {
        setConfirmPopup((prev) => ({ ...prev, open: false }));
        setIsLoading(true);
        try {
          const response = await deleteLicenseDetails(id);
          if (response.data.success) {
            setLocalLicenses((prev) =>
              prev.filter((lic: any) => lic.id !== id),
            );
            refresh();
          } else {
            setInfoPopup({
              open: true,
              title: "Error",
              note: "Failed to delete license. Please try again.",
            });
          }
        } catch (error) {
          console.error("ERROR IN DELETING LICENSE::", error);
          setInfoPopup({
            open: true,
            title: "Error",
            note: "An error occurred while deleting the license.",
          });
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    handleLicenseChange("file", file);
  };

  const handleUploadProviderDocument = async (file: File) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("documents", file);
      const response = await uploadProviderDocument(formData);
      if (response.data.success) {
        setAlertMessage("Document uploaded successfully.");
        setAlertType("success");
        setAlertOpen(true);
        refresh();
      }
    } catch (error: any) {
      console.error("ERROR uploading provider documents::", error);
      const errorData = error?.response?.data;
      if (errorData?.error === "FILE_TOO_LARGE") {
        setAlertMessage(`${errorData.message}. ${errorData.details}`);
      } else {
        setAlertMessage(errorData?.message || "Error uploading document.");
      }
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return dayjs(dateString).format("DD/MM/YYYY");
  };

  const handleDownload = async (url: string, fileName: string) => {
    if (!url) return;
    setIsLoading(true);
    try {
      const isExternal =
        url.startsWith("http") && !url.includes(window.location.host);

      // Use the raw axios library for external links to bypass our custom interceptors
      // which add Authorization/source headers (these break S3 pre-signed signatures)
      const axiosInstance = isExternal ? axios : customAxios;

      const response = await axiosInstance({
        url,
        method: "GET",
        responseType: "blob",
      });

      // Extract extension from URL if not already in fileName
      let finalFileName = fileName;
      const urlPath = url.split("?")[0];
      const extension = urlPath.split(".").pop();
      if (
        extension &&
        extension.length <= 5 &&
        !fileName.toLowerCase().endsWith(`.${extension.toLowerCase()}`)
      ) {
        finalFileName = `${fileName}.${extension}`;
      }

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", finalFileName || "document");
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback: If CORS/Security blocks the blob fetch, open in new tab
      // The user can then save it manually (Ctrl+S or Download button in browser)
      window.open(url, "_blank");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box className="documentation">
        {/* Compliance Section */}
        <Box className="documentation__section documentation__section--left">
          <Typography variant="h6" className="documentation__title">
            Compliance
          </Typography>
          <Typography variant="body2" className="documentation__subtitle">
            Add compliance documents
          </Typography>

          {/* Table & btn */}
          <Box className="documentation__license-table">
            {/* Table Header */}
            <Box className="documentation__license-header">
              <Typography>License Number</Typography>
              <Typography>Expiration Date</Typography>
              <Typography>Document</Typography>
              <Box />
              <Box />
            </Box>

            {/* Active Entry Row - ONLY IF showForm is true */}
            {showForm && (
              <Box className="documentation__license-row documentation__license-row--form">
                <input
                  type="text"
                  placeholder="Enter Number"
                  value={newLicense.licenceNumber}
                  maxLength={30}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/[^a-zA-Z0-9]/g, "")
                      .slice(0, 30);
                    handleLicenseChange("licenceNumber", value);
                  }}
                  className="documentation__license-text"
                />
                <Box className="documentation__license-datepicker-container">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={newLicense.expirationDate}
                      desktopModeMediaQuery="@media (min-width: 0px)"
                      onChange={(date) => {
                        handleLicenseChange("expirationDate", date);
                        if (date && date.isValid()) {
                          if (date.isBefore(dayjs(), "day")) {
                            setDateError("Date cannot be in the past");
                          } else {
                            setDateError(null);
                          }
                        } else if (date === null) {
                          setDateError(null);
                        } else {
                          setDateError("Enter valid date");
                        }
                      }}
                      onError={(reason) => {
                        if (reason === "minDate" || reason === "disablePast") {
                          setDateError("Date cannot be in the past");
                        } else if (reason === "invalidDate") {
                          setDateError("Enter valid date");
                        } else if (!reason) {
                          // dateError is already handled in onChange for valid dates
                        }
                      }}
                      format="DD/MM/YYYY"
                      minDate={dayjs()}
                      disablePast
                      views={["year", "month", "day"]}
                      slotProps={{
                        textField: {
                          placeholder: "dd/mm/yyyy",
                          variant: "standard",
                          error: !!dateError,
                          helperText: dateError,
                          sx: {
                            width: "100%",
                            "& .MuiInput-root": {
                              fontSize: "14px",
                              "&:before, &:after": { borderBottom: "none" },
                              "&:hover:not(.Mui-disabled):before": {
                                borderBottom: "none",
                              },
                            },
                            "& .MuiInputBase-input": {
                              textAlign: "center",
                              padding: "8px",
                            },
                            "& .MuiFormHelperText-root": {
                              textAlign: "center",
                              fontSize: "10px",
                              margin: 0,
                              position: "absolute",
                              bottom: "-15px",
                              width: "100%",
                              whiteSpace: "nowrap",
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                <Box className="documentation__license-upload-wrapper">
                  <label>
                    <TruncatedTooltip title={newLicense.file ? newLicense.file.name : "Upload File"} className="documentation__license-upload-text">
                      {newLicense.file ? newLicense.file.name : "Upload File"}
                    </TruncatedTooltip>
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                    />
                  </label>
                </Box>
                <Box /> {/* Column 4 empty for form */}
                <AddIcon
                  className="documentation__row-action-icon documentation__row-action-icon--add"
                  onClick={handleAddLicense}
                />
              </Box>
            )}

            {/* Existing Licenses from API */}
            {localLicenses?.map((license: any) => (
              <Box key={license.id} className="documentation__license-row">
                <Tooltip title={license.licenceNumber} arrow>
                  <Typography className="documentation__license-text-display">
                    {license.licenceNumber}
                  </Typography>
                </Tooltip>
                <Typography className="documentation__license-text-display">
                  {formatDate(license.expiryDate)}
                </Typography>
                <Button
                  component="a"
                  href={license.document?.[0]?.url}
                  target="_blank"
                  className="documentation__license-upload-text"
                  sx={{ textTransform: "none" }}
                >
                  View Document
                </Button>
                <FileDownloadOutlined
                  className="documentation__row-action-icon documentation__row-action-icon--download"
                  onClick={() => {
                    const url = license.document?.[0]?.url;
                    const fileName = `License_${license.licenceNumber}`;
                    handleDownload(url, fileName);
                  }}
                />
                <Close
                  className="documentation__row-action-icon documentation__row-action-icon--clear"
                  onClick={() => handleDeleteLicense(license.id)}
                />
              </Box>
            ))}
          </Box>

          <Button
            variant="outlined"
            className="documentation__add-button"
            onClick={() => setShowForm(true)}
            disabled={showForm}
          >
            <AddIcon fontSize="small" />
            Add Another License
          </Button>
        </Box>

        {/* Documentation Section */}
        <Box className="documentation__section documentation__section--right">
          {/* Provider-Uploaded Documents */}
          <Box className="documentation__uploaded">
            <Typography variant="h6" className="documentation__title">
              Documentation
            </Typography>
            <Typography variant="body2" className="documentation__subtitle">
              Add Documents
            </Typography>
            <Box className="documentation__uploaded-header" mt={2}>
              <Typography className="documentation__uploaded-title">
                Provider-Uploaded Documents
              </Typography>
              <label>
                <Button component="span" className="documentation__upload-btn">
                  Upload
                </Button>
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadProviderDocument(file);
                    e.target.value = "";
                  }}
                />
              </label>
            </Box>

            {/* AFP Documents Section
          <Box className="documentation__afp">
            <Box className="documentation__afp-header">
              <Typography className="documentation__afp-title">
                AFP Documents
              </Typography>
              <Typography className="documentation__afp-count">
                {data?.afpDocuments?.length || 0} Documents
              </Typography>
            </Box>

            <Box className="documentation__afp-list">
              {data?.afpDocuments?.map((doc: any) => (
                <Box key={doc.fileId} className="documentation__afp-item">
                  <Box className="documentation__afp-left">
                    <Box className="documentation__afp-info">
                      <Typography className="documentation__afp-doc-title">
                        {doc.fileName}
                      </Typography>
                      <Typography className="documentation__afp-doc-desc">
                        Review and acknowledge this document
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    component="a"
                    href={doc.url}
                    target="_blank"
                    className="documentation__afp-view"
                  >
                    View
                  </Button>
                </Box>
              ))}
            </Box>
          </Box> */}

            <Box className="documentation__uploaded-list">
              {(data?.providerDocuments || data?.partnerDocuments)?.map(
                (doc: any) => (
                  <Box
                    key={doc.fileId}
                    className="documentation__uploaded-item"
                  >
                    <Box className="documentation__uploaded-left">
                      <Box className="documentation__file-icon">
                        <DescriptionOutlined />
                      </Box>
                      <Box className="documentation__file-info">
                        <TruncatedTooltip title={doc.fileName || doc.name} className="documentation__file-name">
                          {doc.fileName || doc.name}
                        </TruncatedTooltip>
                        <Typography className="documentation__file-date">
                          Uploaded on{" "}
                          {dayjs(doc.uploadedAt).format("MMMM DD, YYYY")}
                        </Typography>
                      </Box>
                    </Box>
                    <Box className="documentation__uploaded-right">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDocMenuClick(e, doc);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                    {/* ActionsMenu for uploaded documents */}
                    <ActionsMenu
                      anchorEl={menuAnchorEl}
                      onClose={handleDocMenuClose}
                      onOptionClick={handleDocMenuOptionClick}
                      options={getDocMenuOptions}
                      width="150px"
                    />
                  </Box>
                ),
              )}
            </Box>
            {(!data?.providerDocuments?.length && !data?.partnerDocuments?.length) &&
              <Typography variant="body2" className="documentation__no-upload">
                  No documents uploaded yet
              </Typography>
            }
          </Box>
        </Box>

        <InfoPopup
          open={infoPopup.open}
          title={infoPopup.title}
          note={infoPopup.note}
          onClose={() => setInfoPopup((prev) => ({ ...prev, open: false }))}
        />

        <ActionConfirmationPopup
          open={confirmPopup.open}
          title={confirmPopup.title}
          note={confirmPopup.note}
          onClose={() => setConfirmPopup((prev) => ({ ...prev, open: false }))}
          onConfirm={confirmPopup.onOkay}
          confirmButtonText="Delete"
          variant="danger"
          type="delete"
        />
      </Box>
      <AlertComponent
        open={alertOpen}
        message={alertMessage || ""}
        type={alertType}
        onClose={() => setAlertOpen(false)}
      />
    </>
  );
};

export default Documentation;
